import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { useMemo, useState } from "react";
import { formatUzPhone } from "../../../phone";
import type { SupplierOut } from "../api";
import { t as translate } from "../preferences";
import { filterSuppliers, type SupplierMode } from "../supplierSelection";

export function SupplierCombobox({
  suppliers,
  mode,
  supplierId,
  onSelect,
}: {
  suppliers: SupplierOut[];
  mode: SupplierMode;
  supplierId: string;
  onSelect: (mode: SupplierMode, supplierId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const visible = useMemo(() => filterSuppliers(suppliers, query), [query, suppliers]);
  const selected = mode === "none"
    ? translate("Ko‘rsatilmagan")
    : mode === "new"
      ? translate("Yangi")
      : suppliers.find((supplier) => String(supplier.id) === supplierId)?.name ?? translate("Tanlang");

  function choose(nextMode: SupplierMode, nextId = "") {
    onSelect(nextMode, nextId);
    setOpen(false);
    setQuery("");
  }

  return <div onBlurCapture={(event) => {
    const next = event.relatedTarget as Node | null;
    if (!next || !event.currentTarget.contains(next)) setOpen(false);
  }}>
    <span className="m-field-label">{translate("Yetkazib beruvchi")}</span>
    <button
      type="button"
      className="m-text-input m-flex m-items-center m-justify-between m-gap-2 m-text-left"
      role="combobox"
      aria-label={translate("Yetkazib beruvchi")}
      aria-expanded={open}
      onClick={() => setOpen((current) => !current)}
    >
      <span className="m-truncate">{selected}</span>
      {open ? <ChevronUp className="m-size-5 m-shrink-0" /> : <ChevronDown className="m-size-5 m-shrink-0" />}
    </button>
    {open ? <div className="m-mt-1 m-rounded-xl m-border m-border-border m-bg-surface m-p-2">
      <input
        type="search"
        className="m-text-input m-mb-2"
        aria-label={translate("Yetkazib beruvchi qidirish")}
        placeholder={translate("Nomi yoki telefon raqami")}
        autoFocus
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <div role="listbox" aria-label={translate("Yetkazib beruvchi")} className="m-max-h-60 m-overflow-y-auto m-rounded-xl m-border m-border-border">
        <SupplierOption selected={mode === "none"} label={translate("Ko‘rsatilmagan")} onClick={() => choose("none")} />
        <SupplierOption selected={mode === "new"} label={translate("Yangi")} onClick={() => choose("new")} />
        {visible.map((supplier) => <SupplierOption
          key={supplier.id}
          selected={mode === "existing" && supplierId === String(supplier.id)}
          label={supplier.name}
          detail={formatUzPhone(supplier.phone)}
          onClick={() => choose("existing", String(supplier.id))}
        />)}
      </div>
      {query.trim() && !visible.length ? <p className="m-p-3 m-text-center m-text-sm m-text-muted">{translate("Mos yetkazib beruvchi topilmadi.")}</p> : null}
    </div> : null}
  </div>;
}

function SupplierOption({ selected, label, detail, onClick }: { selected: boolean; label: string; detail?: string; onClick: () => void }) {
  return <button type="button" role="option" aria-selected={selected} className="m-flex m-min-h-12 m-w-full m-items-center m-gap-2 m-border-b m-border-border m-px-3 m-py-2 m-text-left last:m-border-b-0" onClick={onClick}>
    <Check className={`m-size-5 m-shrink-0 m-text-primary ${selected ? "m-opacity-100" : "m-opacity-0"}`} />
    <span className="m-min-w-0"><span className="m-block m-truncate">{label}</span>{detail ? <span className="m-block m-text-sm m-text-muted">{detail}</span> : null}</span>
  </button>;
}
